import { useState, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { GlassCard, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type InjurySelection,
  type InjuryFlag,
  type FitnessGoal,
  type WeekDay,
} from "@/lib/store";

const INJURY_OPTIONS: { flag: InjuryFlag; title: string; sub: string; emoji: string }[] = [
  { flag: "joints",       title: "Joints",         sub: "Knees, hips, shoulders, wrists, ankles", emoji: "🦵" },
  { flag: "spine",        title: "Spine",          sub: "Low back, neck, disc issues, scoliosis",  emoji: "🌿" },
  { flag: "pelvic-floor", title: "Pelvic floor",   sub: "Diastasis, prolapse, leaking, heaviness", emoji: "🤍" },
  { flag: "other",        title: "Something else", sub: "Tendinitis, hernia, recovery from surgery", emoji: "🩹" },
];

const MOVEMENT_GOAL_OPTIONS: { value: FitnessGoal; label: string; emoji: string; sub: string }[] = [
  { value: "build-strength",       label: "Build strength",          emoji: "💪", sub: "Heavier compounds, NASM 4-week blocks" },
  { value: "lose-weight",          label: "Tend my weight gently",   emoji: "🌾", sub: "More volume, kettlebells, intervals" },
  { value: "flexibility",          label: "Mobility and flexibility", emoji: "🧘", sub: "Hip openers, T-spine, end-range work" },
  { value: "postpartum-recovery",  label: "Postpartum recovery",     emoji: "🌷", sub: "GGS V2 protocol, pelvic floor first" },
];

const WEEKDAY_OPTIONS: { value: WeekDay; label: string; short: string }[] = [
  { value: 0, label: "Sun", short: "S" },
  { value: 1, label: "Mon", short: "M" },
  { value: 2, label: "Tue", short: "T" },
  { value: 3, label: "Wed", short: "W" },
  { value: 4, label: "Thu", short: "T" },
  { value: 5, label: "Fri", short: "F" },
  { value: 6, label: "Sat", short: "S" },
];

const MINUTE_OPTIONS = [20, 30, 45, 60];

type Tab = "injuries" | "goals" | "schedule";

export default function PlanEditor() {
  const [, setLocation] = useLocation();
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: setInjuries/setGoals/setSchedule
  // below all WRITE with this id, onto whichever profile it resolves to.
  const userId = me?.id ?? null;
  const profile = useStoreQuery(() => (userId ? queries.movementProfile(userId) : null));
  const mesocycle = useStoreQuery(() => (userId ? queries.mesocycleSummary(userId) : null));

  const setInjuries = useStoreMutation(mutations.setInjuries);
  const setGoals = useStoreMutation(mutations.setFitnessGoals);
  const setSchedule = useStoreMutation(mutations.setSchedule);

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to edit your plan, sister.
        </p>
      </div>
    );
  }

  // Pre-onboarding gate: send back to Settings with a CTA to Onboarding
  if (!profile || !profile.completed) {
    return (
      <div className="px-5 pt-4 pb-28 space-y-4" style={{ background: "var(--mnf-cream)" }}>
        <button
          onClick={() => setLocation("/settings")}
          data-testid="button-back-plan"
          className="flex items-center gap-2 mnf-body"
          style={{ color: "var(--mnf-f)", padding: "6px 0", background: "none", border: "none", cursor: "pointer" }}
        >
          <Icon name="arrow_back" size={16} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Settings</span>
        </button>
        <GlassCard organic padding={18}>
          <p className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-f)" }}>
            Set up your plan first
          </p>
          <p className="mnf-body mt-2" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
            Coach Kiley needs to know about injuries, goals, and your week before she can edit anything. Walk through onboarding once and the editor opens up.
          </p>
          <Link href="/onboarding" data-testid="link-plan-onboarding">
            <Btn variant="primary" size="md" className="mt-3">
              Open onboarding <Icon name="arrow_forward" size={14} />
            </Btn>
          </Link>
        </GlassCard>
      </div>
    );
  }

  return (
    <PlanEditorBody
      userId={userId}
      injuries={profile.injuries}
      goals={profile.goals}
      days={profile.schedule.days}
      minutes={profile.schedule.minutesPerDay}
      mesocycleWeek={mesocycle?.week ?? null}
      mesocycleBlock={mesocycle?.block ?? null}
      onSaveInjuries={(v) => setInjuries(userId, v)}
      onSaveGoals={(v) => setGoals(userId, v)}
      onSaveSchedule={(d, m) => setSchedule(userId, { days: d, minutesPerDay: m })}
      onClose={() => setLocation("/settings")}
    />
  );
}

function PlanEditorBody({
  userId,
  injuries,
  goals,
  days,
  minutes,
  mesocycleWeek,
  mesocycleBlock,
  onSaveInjuries,
  onSaveGoals,
  onSaveSchedule,
  onClose,
}: {
  userId: string;
  injuries: InjurySelection[];
  goals: FitnessGoal[];
  days: WeekDay[];
  minutes: number;
  mesocycleWeek: number | null;
  mesocycleBlock: string | null;
  onSaveInjuries: (v: InjurySelection[]) => void;
  onSaveGoals: (v: FitnessGoal[]) => void;
  onSaveSchedule: (d: WeekDay[], m: number) => void;
  onClose: () => void;
}) {
  void userId;
  const [tab, setTab] = useState<Tab>("injuries");
  const [draftInj, setDraftInj] = useState<InjurySelection[]>(injuries);
  const [draftGoals, setDraftGoals] = useState<FitnessGoal[]>(goals);
  const [draftDays, setDraftDays] = useState<WeekDay[]>(days);
  const [draftMinutes, setDraftMinutes] = useState<number>(minutes);
  const [savedFlash, setSavedFlash] = useState<Tab | null>(null);

  const dirty = useMemo(() => {
    const sameArr = <T,>(a: T[], b: T[]) =>
      a.length === b.length && a.every((x) => b.includes(x));
    const sameInj =
      draftInj.length === injuries.length &&
      draftInj.every((d) => {
        const m = injuries.find((i) => i.flag === d.flag);
        return m && m.detail === d.detail && m.paused === d.paused;
      });
    return {
      injuries: !sameInj,
      goals: !sameArr(draftGoals, goals),
      schedule: !sameArr(draftDays, days) || draftMinutes !== minutes,
    };
  }, [draftInj, draftGoals, draftDays, draftMinutes, injuries, goals, days, minutes]);

  const flash = (which: Tab) => {
    setSavedFlash(which);
    setTimeout(() => setSavedFlash((cur) => (cur === which ? null : cur)), 1400);
  };

  const saveAll = () => {
    if (dirty.injuries) onSaveInjuries(draftInj);
    if (dirty.goals) onSaveGoals(draftGoals);
    if (dirty.schedule) onSaveSchedule(draftDays, draftMinutes);
    flash(tab);
  };

  const anyDirty = dirty.injuries || dirty.goals || dirty.schedule;

  return (
    <div className="flex flex-col h-full" style={{ background: "var(--mnf-cream)" }}>
      {/* Header */}
      <div
        className="px-4 pt-3 pb-4"
        style={{
          background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
          color: "#F5F0E8",
          borderRadius: "0 0 24px 24px",
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            data-testid="button-close-plan-editor"
            className="flex items-center justify-center"
            style={{
              width: 36, height: 36, borderRadius: 12,
              background: "rgba(245,240,232,0.12)", color: "#F5F0E8",
              border: "none", cursor: "pointer",
            }}
            aria-label="Close"
          >
            <Icon name="arrow_back" size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="mnf-eyebrow" style={{ color: "rgba(245,240,232,0.7)" }}>Coach plan</p>
            <p className="mnf-serif italic" style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.1 }}>
              Edit my plan
            </p>
          </div>
          {mesocycleWeek && mesocycleBlock && (
            <span
              className="mnf-body"
              style={{
                padding: "4px 10px", borderRadius: 999,
                background: "rgba(245,240,232,0.14)",
                border: "1px solid rgba(245,240,232,0.22)",
                color: "rgba(245,240,232,0.9)",
                fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              }}
            >
              Wk {mesocycleWeek} · Blk {mesocycleBlock}
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 mt-4" style={{ background: "rgba(245,240,232,0.1)", padding: 4, borderRadius: 14 }}>
          <TabButton label="Injuries"  active={tab === "injuries"} dirty={dirty.injuries} onClick={() => setTab("injuries")} />
          <TabButton label="Goals"     active={tab === "goals"}    dirty={dirty.goals}    onClick={() => setTab("goals")} />
          <TabButton label="Schedule"  active={tab === "schedule"} dirty={dirty.schedule} onClick={() => setTab("schedule")} />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4 mnf-scroll">
        {tab === "injuries" && (
          <InjuriesTab
            value={draftInj}
            onChange={setDraftInj}
            saved={savedFlash === "injuries"}
          />
        )}
        {tab === "goals" && (
          <GoalsTab
            value={draftGoals}
            onChange={setDraftGoals}
            saved={savedFlash === "goals"}
          />
        )}
        {tab === "schedule" && (
          <ScheduleTab
            days={draftDays}
            minutes={draftMinutes}
            onChange={(d, m) => { setDraftDays(d); setDraftMinutes(m); }}
            saved={savedFlash === "schedule"}
          />
        )}
      </div>

      {/* Footer: save / cancel */}
      <div
        className="px-4 pt-3 pb-4 flex items-center gap-2"
        style={{
          background: "rgba(245,240,232,0.95)",
          backdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(30,74,53,0.06)",
        }}
      >
        <Btn
          variant="ghost"
          size="md"
          onClick={onClose}
          data-testid="button-cancel-plan"
        >
          Done
        </Btn>
        <div className="flex-1" />
        <Btn
          variant="primary"
          size="md"
          onClick={saveAll}
          disabled={!anyDirty}
          data-testid="button-save-plan"
        >
          {anyDirty ? "Save changes" : "All saved"}
          {anyDirty && <Icon name="check" size={14} />}
        </Btn>
      </div>
    </div>
  );
}

function TabButton({
  label, active, dirty, onClick,
}: {
  label: string;
  active: boolean;
  dirty: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={`tab-${label.toLowerCase()}`}
      className="flex-1 flex items-center justify-center gap-1.5 hover-elevate active-elevate-2"
      style={{
        padding: "8px 0",
        borderRadius: 10,
        background: active ? "#F5F0E8" : "transparent",
        color: active ? "var(--mnf-fd)" : "rgba(245,240,232,0.85)",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span className="mnf-body" style={{ fontSize: 12.5, fontWeight: active ? 700 : 600, letterSpacing: "0.02em" }}>
        {label}
      </span>
      {dirty && (
        <span
          aria-label="Unsaved"
          style={{
            width: 6, height: 6, borderRadius: 999,
            background: active ? "var(--mnf-t)" : "var(--mnf-fiberBorder)",
          }}
        />
      )}
    </button>
  );
}

/* ============================== Injuries tab ============================== */

function InjuriesTab({
  value, onChange, saved,
}: {
  value: InjurySelection[];
  onChange: (v: InjurySelection[]) => void;
  saved: boolean;
}) {
  const toggle = (flag: InjuryFlag) => {
    const exists = value.find((v) => v.flag === flag);
    if (exists) {
      onChange(value.filter((v) => v.flag !== flag));
    } else {
      onChange([...value, { flag, detail: "", paused: false }]);
    }
  };
  const setDetail = (flag: InjuryFlag, detail: string) => {
    onChange(value.map((v) => (v.flag === flag ? { ...v, detail } : v)));
  };
  const togglePaused = (flag: InjuryFlag) => {
    onChange(value.map((v) => (v.flag === flag ? { ...v, paused: !v.paused } : v)));
  };

  return (
    <div className="space-y-3">
      <SectionIntro
        title="Injuries to work around"
        sub="Active flags swap or modify any movement that touches them. Pause a flag when it's healing well to unlock more exercises."
        saved={saved}
      />
      <div className="space-y-2.5">
        {INJURY_OPTIONS.map((opt) => {
          const selected = value.find((v) => v.flag === opt.flag);
          const active = !!selected;
          return (
            <div key={opt.flag}>
              <button
                onClick={() => toggle(opt.flag)}
                data-testid={`button-injury-${opt.flag}`}
                className="w-full flex items-center gap-4 text-left transition-all hover-elevate active-elevate-2"
                style={{
                  padding: "12px 14px",
                  borderRadius: 16,
                  background: active ? "rgba(192,120,74,0.10)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${active ? "var(--mnf-t)" : "rgba(30,74,53,0.06)"}`,
                }}
              >
                <span style={{ fontSize: 22 }} aria-hidden>{opt.emoji}</span>
                <div className="flex-1">
                  <p className="mnf-body" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--mnf-fd)" }}>{opt.title}</p>
                  <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}>{opt.sub}</p>
                </div>
                <Icon name={active ? "check_circle" : "add_circle"} size={20} weight={500} />
              </button>
              {active && (
                <div className="mt-1.5 mb-1 px-1 flex items-center gap-2">
                  <input
                    data-testid={`input-injury-detail-${opt.flag}`}
                    value={selected?.detail ?? ""}
                    onChange={(e) => setDetail(opt.flag, e.target.value)}
                    placeholder="A short note for Coach Kiley"
                    className="flex-1 mnf-body outline-none bg-transparent"
                    style={{
                      fontSize: 12.5, color: "var(--mnf-fd)",
                      borderBottom: "1px solid rgba(30,74,53,0.12)",
                      padding: "6px 4px",
                    }}
                  />
                  <button
                    onClick={() => togglePaused(opt.flag)}
                    data-testid={`toggle-pause-${opt.flag}`}
                    className="mnf-body hover-elevate active-elevate-2 shrink-0"
                    style={{
                      padding: "5px 10px", borderRadius: 999,
                      background: selected?.paused ? "rgba(30,74,53,0.06)" : "var(--mnf-f)",
                      color: selected?.paused ? "var(--mnf-tg)" : "#F5F0E8",
                      fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                      border: `1px solid ${selected?.paused ? "rgba(30,74,53,0.14)" : "var(--mnf-f)"}`,
                      cursor: "pointer",
                    }}
                  >
                    {selected?.paused ? "Paused" : "Active"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {value.length === 0 && (
        <div
          className="flex items-start gap-3 p-3"
          style={{ background: "var(--mnf-fiberBg)", border: "1px solid var(--mnf-fiberBorder)", borderRadius: 14 }}
        >
          <span style={{ fontSize: 20 }} aria-hidden>🌿</span>
          <p className="mnf-body italic" style={{ fontSize: 12, color: "#2C6248", lineHeight: 1.5 }}>
            All clear. Coach Kiley will use the full library.
          </p>
        </div>
      )}
    </div>
  );
}

/* ================================ Goals tab =============================== */

function GoalsTab({
  value, onChange, saved,
}: {
  value: FitnessGoal[];
  onChange: (v: FitnessGoal[]) => void;
  saved: boolean;
}) {
  const toggle = (g: FitnessGoal) => {
    if (value.includes(g)) onChange(value.filter((x) => x !== g));
    else onChange([...value, g]);
  };
  return (
    <div className="space-y-3">
      <SectionIntro
        title="What does your strength season ask for?"
        sub="Pick one or two. Your first goal drives the set and rep scheme. Reorder by tapping off then on again."
        saved={saved}
      />
      <div className="space-y-2.5">
        {MOVEMENT_GOAL_OPTIONS.map((g, idx) => {
          const active = value.includes(g.value);
          const orderIdx = value.indexOf(g.value);
          return (
            <button
              key={g.value}
              onClick={() => toggle(g.value)}
              data-testid={`button-goal-${g.value}`}
              className="w-full flex items-center gap-4 text-left transition-all hover-elevate active-elevate-2"
              style={{
                padding: "12px 14px",
                borderRadius: 16,
                background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
                border: `1px solid ${active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)"}`,
                color: active ? "#F5F0E8" : "var(--mnf-fd)",
              }}
            >
              <span style={{ fontSize: 22 }} aria-hidden>{g.emoji}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="mnf-body" style={{ fontSize: 13.5, fontWeight: 700 }}>{g.label}</p>
                  {active && orderIdx === 0 && (
                    <span
                      className="mnf-body"
                      style={{
                        padding: "1.5px 7px", borderRadius: 999,
                        background: "rgba(245,240,232,0.18)",
                        color: "#F5F0E8",
                        fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                      }}
                    >
                      Primary
                    </span>
                  )}
                </div>
                <p className="mnf-body" style={{ fontSize: 11.5, opacity: active ? 0.85 : 0.7, lineHeight: 1.4 }}>
                  {g.sub}
                </p>
              </div>
              <Icon name={active ? "check_circle" : "add_circle"} size={20} weight={500} />
              <span className="sr-only">{idx}</span>
            </button>
          );
        })}
      </div>
      {value.length === 0 && (
        <div
          className="flex items-start gap-3 p-3"
          style={{ background: "rgba(192,120,74,0.10)", border: "1px solid rgba(192,120,74,0.30)", borderRadius: 14 }}
        >
          <span style={{ fontSize: 20 }} aria-hidden>🌾</span>
          <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
            Pick at least one goal so Coach knows how to build your blocks.
          </p>
        </div>
      )}
    </div>
  );
}

/* =============================== Schedule tab ============================= */

function ScheduleTab({
  days, minutes, onChange, saved,
}: {
  days: WeekDay[];
  minutes: number;
  onChange: (d: WeekDay[], m: number) => void;
  saved: boolean;
}) {
  const toggleDay = (d: WeekDay) => {
    if (days.includes(d)) onChange(days.filter((x) => x !== d), minutes);
    else onChange([...days, d].sort((a, b) => a - b) as WeekDay[], minutes);
  };
  const splitCopy =
    days.length === 0 ? "Pick at least one day."
      : days.length <= 3 ? `${days.length} day${days.length === 1 ? "" : "s"} a week, full-body sessions.`
        : days.length === 4 ? "4 days a week, upper and lower split."
          : `${days.length} days a week, push, pull, legs.`;
  const minuteCopy =
    minutes >= 45 ? "Two compounds, two accessories, core, and mobility."
      : minutes >= 30 ? "One compound, one accessory, core, and mobility."
        : "One compound, one accessory, and core. Mobility on rest days.";

  return (
    <div className="space-y-3">
      <SectionIntro
        title="When can you train?"
        sub="Days plus minutes per day. Coach Kiley reshapes the split when you change either."
        saved={saved}
      />

      <GlassCard padding={16}>
        <p className="mnf-eyebrow mb-3" style={{ color: "var(--mnf-tg)" }}>Training days</p>
        <div className="flex justify-between gap-1.5">
          {WEEKDAY_OPTIONS.map((d) => {
            const active = days.includes(d.value);
            return (
              <button
                key={d.value}
                onClick={() => toggleDay(d.value)}
                data-testid={`button-day-${d.value}`}
                className="flex-1 flex flex-col items-center justify-center transition-all hover-elevate active-elevate-2"
                style={{
                  padding: "10px 0",
                  borderRadius: 14,
                  background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${active ? "var(--mnf-f)" : "rgba(30,74,53,0.10)"}`,
                  color: active ? "#F5F0E8" : "var(--mnf-fd)",
                  minHeight: 54,
                  cursor: "pointer",
                }}
              >
                <span className="mnf-serif" style={{ fontSize: 17, fontWeight: 500 }}>{d.short}</span>
                <span
                  className="mnf-body"
                  style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", opacity: active ? 0.85 : 0.6 }}
                >
                  {d.label}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mnf-body mt-3" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          {splitCopy}
        </p>
      </GlassCard>

      <GlassCard padding={16}>
        <p className="mnf-eyebrow mb-3" style={{ color: "var(--mnf-tg)" }}>Minutes per day</p>
        <div className="grid grid-cols-4 gap-2">
          {MINUTE_OPTIONS.map((m) => {
            const active = minutes === m;
            return (
              <button
                key={m}
                onClick={() => onChange(days, m)}
                data-testid={`button-minutes-${m}`}
                className="flex flex-col items-center justify-center transition-all hover-elevate active-elevate-2"
                style={{
                  padding: "10px 0",
                  borderRadius: 14,
                  background: active ? "var(--mnf-t)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${active ? "var(--mnf-t)" : "rgba(30,74,53,0.10)"}`,
                  color: active ? "#fff" : "var(--mnf-fd)",
                  cursor: "pointer",
                }}
              >
                <span className="mnf-serif" style={{ fontSize: 20, fontWeight: 500 }}>{m}</span>
                <span
                  className="mnf-body"
                  style={{ fontSize: 9.5, letterSpacing: "0.06em", textTransform: "uppercase", opacity: active ? 0.9 : 0.6 }}
                >
                  min
                </span>
              </button>
            );
          })}
        </div>
        <p className="mnf-body mt-3" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          {minuteCopy}
        </p>
      </GlassCard>
    </div>
  );
}

/* =============================== Shared bits ============================== */

function SectionIntro({
  title, sub, saved,
}: {
  title: string;
  sub: string;
  saved: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-1">
        <p className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}>
          {title}
        </p>
        <p className="mnf-body mt-1" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          {sub}
        </p>
      </div>
      {saved && (
        <span
          className="mnf-body inline-flex items-center gap-1 shrink-0"
          style={{
            padding: "4px 9px", borderRadius: 999,
            background: "var(--mnf-fiberBg)",
            color: "#2C6248",
            border: "1px solid var(--mnf-fiberBorder)",
            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
          }}
          data-testid="badge-saved"
        >
          <Icon name="check" size={11} />
          Saved
        </span>
      )}
    </div>
  );
}
